import { formatMoney } from './currency-format';
import { isClosed, number } from './journal-metrics';
import { moneyReturn, percent, profileSlug, shareMetrics, tradeHero, tradeR, tradesInPeriod } from './share-utils';

export const COMPOSER_FORMATS = {
  landscape: { width: 1200, height: 675, label: 'Landscape', ratio: '16:9', hint: 'A wide card for X, YouTube and desktop sharing.' },
  square: { width: 1080, height: 1080, label: 'Square', ratio: '1:1', hint: 'A balanced fit for feeds, Discord and profile posts.' },
  portrait: { width: 1080, height: 1350, label: 'Portrait', ratio: '4:5', hint: 'More screen space in Instagram and other mobile feeds.' },
  story: { width: 1080, height: 1920, label: 'Story', ratio: '9:16', hint: 'Full-height framing for Stories, Reels and TikTok.' },
};
export const COMPOSER_BACKGROUNDS = [
  ['aurora', 'Aurora', 'A slow, luminous current'],
  ['market', 'Market', 'Moving contours and a quiet grid'],
  ['silk', 'Silk', 'Soft waves of colour'],
  ['plain', 'Still', 'The essentials, with no motion'],
];
export const COMPOSER_QUALITIES = {
  high: { label: 'High · recommended', photoEdge: 3840, videoEdge: 2560, gifEdge: 1920, gifFPS: 25, bitrate: 24000000 },
  ultra: { label: 'Maximum detail', photoEdge: 3840, videoEdge: 3840, gifEdge: 2560, gifFPS: 25, bitrate: 48000000 },
};
export const MAX_VIDEO_SECONDS = 40;
export const COMPOSER_FONTS = {
  theme: { label: 'Match site', family: '' },
  modern: { label: 'Clean', family: '"Plus Jakarta Sans", sans-serif' },
  condensed: { label: 'Condensed', family: '"Barlow Condensed", sans-serif' },
  mono: { label: 'Mono', family: '"DM Mono", monospace' },
  editorial: { label: 'Editorial', family: 'Georgia, serif' },
  handwritten: { label: 'Handwritten', family: '"Caveat", cursive' },
};
export const COMPOSER_COLOURS = [['White', '#ffffff'], ['Emerald', '#4ade80'], ['Gold', '#f3cb78'], ['Rose', '#fb7185'], ['Ice', '#8ec5ff'], ['Lilac', '#c4a5ff']];
export const composerColour = value => /^#[0-9a-f]{6}$/i.test(value || '') ? value.toLowerCase() : '';
export function normaliseComposerDesign(value = {}) {
  const align = item => ['left', 'center', 'right'].includes(item) ? item : 'left';
  const safeNumber = (item, fallback, min, max) => Number.isFinite(Number(item)) && item !== null ? clamp(Number(item), min, max) : fallback;
  return {
    font: COMPOSER_FONTS[value?.font] ? value.font : 'theme',
    positiveColour: composerColour(value?.positiveColour), negativeColour: composerColour(value?.negativeColour), neutralColour: composerColour(value?.neutralColour),
    contentAlign: align(value?.contentAlign), contentHeight: ['top', 'middle', 'bottom'].includes(value?.contentHeight) ? value.contentHeight : 'top',
    resultScale: safeNumber(value?.resultScale, 100, 75, 125),
    captionFont: COMPOSER_FONTS[value?.captionFont] ? value.captionFont : 'theme', captionColour: composerColour(value?.captionColour),
    captionAlign: align(value?.captionAlign), captionY: safeNumber(value?.captionY, 100, 0, 100), captionSize: safeNumber(value?.captionSize, 32, 22, 52), captionPlate: value?.captionPlate === true,
  };
}
export function composerFonts(design, theme = {}) {
  const style = normaliseComposerDesign(design);
  return { heading: COMPOSER_FONTS[style.font].family || theme.heading || 'sans-serif', font: COMPOSER_FONTS[style.font].family || theme.font || 'sans-serif', caption: COMPOSER_FONTS[style.captionFont].family || theme.font || 'sans-serif' };
}
export async function prepareComposerFonts(design, theme) {
  if (typeof document === 'undefined' || !document.fonts) return;
  const fonts = composerFonts(design, theme);
  if (document.fonts.load) await Promise.all([document.fonts.load(`700 64px ${fonts.heading}`), document.fonts.load(`500 32px ${fonts.font}`), document.fonts.load(`600 32px ${fonts.caption}`)]);
  await document.fonts.ready;
}
export function composerDimensions(shape, type = 'png', quality = 'high') {
  const size = COMPOSER_FORMATS[shape] || COMPOSER_FORMATS.landscape;
  const settings = COMPOSER_QUALITIES[quality] || COMPOSER_QUALITIES.high;
  const edge = type === 'gif' ? settings.gifEdge : type === 'video' ? settings.videoEdge : settings.photoEdge;
  const scale = Math.min(edge / Math.max(size.width, size.height), type === 'video' ? Math.sqrt(8294400 / (size.width * size.height)) : Infinity);
  const even = value => Math.max(2, Math.round(value / 2) * 2);
  return { width: even(size.width * scale), height: even(size.height * scale) };
}
// Match the screen density without rendering every small moving preview at 4K.
// Inspection gets the full photo-resolution budget; exports remain independent.
export function composerPreviewDimensions(shape, cssWidth, pixelRatio = 1, inspection = false) {
  const size = COMPOSER_FORMATS[shape] || COMPOSER_FORMATS.landscape;
  const ratio = size.height / size.width, edge = inspection ? 3840 : 2560;
  const density = Math.min(3, Math.max(2, Number(pixelRatio) || 1));
  const maximumWidth = edge / Math.max(1, ratio);
  const width = Math.round(Math.min(maximumWidth, Math.max(400, (Number(cssWidth) || 320) * density)));
  return {width, height:Math.round(width * ratio)};
}
export const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
export function countedHero(hero, time) {
  const match = String(hero).match(/^([+−-]?)([£$€¥]?)([\d,.]+)(.*)$/); if (!match || time >= 1.6) return hero;
  const fraction = 1 - Math.pow(1 - clamp(time / 1.6, 0, 1), 3), decimals = match[3].split('.')[1]?.length || 0;
  const number = Number(match[3].replaceAll(',', '')) * fraction;
  return `${match[1]}${match[2]}${number.toLocaleString('en-GB', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${match[4]}`;
}
function executionPrices(trade) {
  return [['Entry', trade?.entry], ['Exit', trade?.closePrice]].filter(([, value]) => value != null && String(value).trim() !== '' && Number.isFinite(Number(value))).map(([label, value]) => [label, Number(value).toLocaleString('en-GB', { minimumFractionDigits: Math.min(8, String(value).split('.')[1]?.length || 0), maximumFractionDigits: 8 })]);
}
export function monthlyReturns(stats, period, unit = '%', accountSize = 0, currency = 'USD') {
  if (period?.type !== 'year') return [];
  const year = period.start.slice(0, 4);
  return Array.from({ length: 12 }, (_, index) => {
    const key = `${year}-${String(index + 1).padStart(2, '0')}`;
    const records = stats.closed.filter(item => item.date?.slice(0, 7) === key);
    const reported = records.filter(item => item.result === 'BE' || (item.returnPct !== '' && item.returnPct != null && Number.isFinite(Number(item.returnPct))));
    const value = reported.reduce((sum, item) => sum + (item.result === 'BE' ? 0 : number(item.returnPct)), 0);
    return { key, label: new Date(Number(year), index, 1, 12).toLocaleDateString('en-GB', { month: 'short' }), value, count: records.length, reported: reported.length,
      display: !reported.length ? '—' : unit === 'currency' ? moneyReturn(value, accountSize, currency) : percent(value),
      compact: unit === 'currency' ? formatMoney(accountSize * value / 100, currency, { signed: true, notation: 'compact', maximumFractionDigits: 1 }) : percent(value) };
  });
}
export function composerModel({ kind, trade, trades = [], period, traderName, owner, originalMember = false, heroUnit = '%', accountSize = 0, currency = 'USD', hideStats = false, showStats = true, caption = '' }) {
  // A profile card never includes private or archived history. Downloads of a
  // selected archived trade are allowed, but do not change its visibility.
  const current = trades.filter(item => !item.archiveFolderId);
  const selected = kind === 'profile' ? current.filter(item => item.isPublic) : tradesInPeriod(current, period);
  const stats = shareMetrics(selected);
  const name = owner ? 'L' : traderName || 'Trader';
  const unit = heroUnit === 'currency' && accountSize > 0 ? 'currency' : heroUnit === 'R' && kind === 'trade' ? 'R' : '%';
  const r = trade && tradeR(trade);
  const result = kind === 'trade' ? tradeHero(trade || {}, unit, accountSize, currency) : { value: unit === 'currency' ? moneyReturn(stats.total, accountSize, currency) : percent(stats.total), unit: '' };
  const hidden = kind === 'profile' && hideStats;
  return {
    kind, name, owner: !!owner, originalMember: originalMember === true, title: kind === 'trade' ? trade?.instrument || 'Trade' : kind === 'profile' ? name : period.label,
    subtitle: kind === 'trade' ? [trade?.direction, trade?.date].filter(Boolean).join(' · ') : kind === 'profile' ? 'PUBLIC TRADING JOURNAL' : 'RECORDED PERFORMANCE',
    hero: hidden ? 'A journal. A journey.' : kind !== 'trade' && !stats.closed.length ? '—' : `${result.value}${result.unit}`,
    heroLabel: hidden ? '' : kind === 'trade' && !isClosed(trade || {}) ? 'OPEN TRADE' : unit === 'currency' ? 'ESTIMATED P&L' : kind === 'trade' ? trade?.result === 'BE' ? 'BREAK EVEN' : 'RECORDED RETURN' : 'NET RETURN',
    negative: kind === 'trade' ? trade?.result === 'LOSS' : stats.total < 0,
    neutral: kind === 'trade' && (trade?.result === 'BE' || !isClosed(trade || {})),
    metrics: hidden || !showStats ? [] : kind === 'trade' ? [
      ...executionPrices(trade), ['Realised R', r == null ? '—' : `${r.toFixed(2)}R`], ['Session', trade?.session || '—'],
    ] : [['Win rate', stats.winRate == null ? '—' : `${stats.winRate.toFixed(1)}%`], ['Trades', `${stats.closed.length} · ${stats.wins}W / ${stats.losses}L`]],
    points: hidden ? [] : stats.points.map(point => point.value),
    months: kind === 'performance' ? monthlyReturns(stats, period, unit, accountSize, currency) : [],
    chartLabel: period?.type === 'year' ? 'MONTHLY NET RETURN' : 'RECORDED EQUITY',
    chartNote: unit === 'currency' ? 'Estimated from the selected account base' : 'Sum of recorded returns · not compounded',
    statsHidden: hidden,
    chart: kind === 'trade' ? trade?.mainChart?.src || trade?.images?.find(item => item?.src)?.src || '' : '',
    caption: String(caption).trim().slice(0, 130),
    canExport: kind === 'profile' || (kind === 'trade' ? !!trade : stats.closed.length > 0),
    selected, stats, unit, profileLink: `https://www.trade-log.co.uk/u/${profileSlug(name)}`,
    filename: kind === 'trade' ? `tradelog-${profileSlug(trade?.instrument) || 'trade'}-${trade?.date || 'record'}` : `tradelog-${kind}-${kind === 'profile' ? profileSlug(name) : period.start || 'all-time'}`,
  };
}
export function validateBackground(file) {
  if (!file) return '';
  const type = backgroundType(file);
  if (!type) return 'Choose a JPG, PNG, WebP, GIF, MP4, WebM or MOV file.';
  const limit = type.startsWith('video/') ? 512 : 80;
  if (file.size > limit * 1024 * 1024) return `Choose a ${type.startsWith('video/') ? 'video' : 'background'} smaller than ${limit} MB.`;
  return '';
}
export function backgroundType(file) {
  const types = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/webm', 'video/quicktime'];
  if (types.includes(file?.type)) return file.type;
  if (file?.type && file.type !== 'application/octet-stream') return '';
  return ({ jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif', mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime' })[file?.name?.split('.').at(-1)?.toLowerCase()] || '';
}
export async function videoImportError(file) {
  let codec = '';
  try {
    for (const part of [file.slice(0, 1024 * 1024), file.slice(Math.max(0, file.size - 1024 * 1024))]) {
      const bytes = new Uint8Array(await part.arrayBuffer());
      for (let i = 0; i + 4 <= bytes.length; i++) {
        if (bytes[i] === 97 && bytes[i + 1] === 118 && bytes[i + 2] === 48 && bytes[i + 3] === 49) codec = 'AV1';
        if (!codec && bytes[i] === 104 && ((bytes[i + 1] === 118 && bytes[i + 2] === 99) || (bytes[i + 1] === 101 && bytes[i + 2] === 118)) && bytes[i + 3] === 49) codec = 'HEVC';
      }
    }
  } catch {}
  return codec ? `This clip uses ${codec}, which this browser cannot decode on this device. Use Chrome or Edge, or an H.264 MP4 copy. The file stays on your device.` : 'This browser could not decode the clip. Choose an H.264 MP4 with AAC audio, or try Chrome or Edge. The file stays on your device.';
}
export function coverPlacement(sourceWidth, sourceHeight, width, height, position = { x: .5, y: .5 }, zoom = 1) {
  const scale = Math.max(width / sourceWidth, height / sourceHeight) * clamp(zoom, 1, 2);
  const w = sourceWidth * scale, h = sourceHeight * scale;
  return { x: (width - w) * clamp(position.x, 0, 1), y: (height - h) * clamp(position.y, 0, 1), width: w, height: h };
}
export const performanceR = stats => stats.closed.reduce((sum, trade) => sum + number(tradeR(trade)), 0);
